/** Native category disclosures: mouse hover enhancement, touch and keyboard fallback. */
class Custom3dCategoryDisclosure extends HTMLElement {
  #controller;
  #closeTimer;
  #hovering = false;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.addEventListener('pointerenter', this.#enter, options);
    this.addEventListener('pointerleave', this.#leave, options);
    this.addEventListener('focusout', this.#focusOut, options);
    this.addEventListener('keydown', this.#keyDown, options);
    this.details?.addEventListener('toggle', this.#toggle, options);
    this.closest('header-drawer')?.querySelector('details')?.addEventListener('toggle', this.#drawerToggle, options);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    clearTimeout(this.#closeTimer);
  }

  get details() {
    return this.querySelector(':scope > details');
  }

  #enter = (event) => {
    if (!this.closest('header-menu') || event.pointerType !== 'mouse' || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.#hovering = true;
    clearTimeout(this.#closeTimer);
    if (this.details) this.details.open = true;
  };

  #leave = (event) => {
    if (!this.closest('header-menu') || event.pointerType !== 'mouse' || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.#hovering = false;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      if (!this.contains(document.activeElement)) this.#close();
    }, 160);
  };

  #focusOut = (event) => {
    // In the touch drawer, close siblings only after the next summary is activated.
    if (!this.closest('header-menu')) return;
    if (!this.contains(event.relatedTarget) && !this.#hovering) this.#close();
  };

  #keyDown = (event) => {
    if (event.key !== 'Escape' || !this.details?.open) return;
    event.preventDefault();
    event.stopPropagation();
    this.#close();
    this.details.querySelector(':scope > summary')?.focus();
  };

  #toggle = () => {
    const details = this.details;
    if (!details) return;
    if (details.open) {
      // Only one branch at each level stays open.
      const item = this.parentElement;
      for (const sibling of item?.parentElement?.children ?? []) {
        if (sibling === item) continue;
        const other = sibling.querySelector(':scope > custom3d-category-disclosure');
        other?.closeBranch();
      }
    } else {
      for (const child of this.querySelectorAll('custom3d-category-disclosure')) child.closeBranch();
    }
  };

  #drawerToggle = (event) => {
    if (!event.target.open) this.#close();
  };

  closeBranch() {
    this.#close();
  }

  #close() {
    clearTimeout(this.#closeTimer);
    if (this.details) this.details.open = false;
    for (const child of this.querySelectorAll('custom3d-category-disclosure')) child.closeBranch();
  }
}

if (!customElements.get('custom3d-category-disclosure')) {
  customElements.define('custom3d-category-disclosure', Custom3dCategoryDisclosure);
}
